import { useMemo } from 'react'
import css from './views.module.css'
import { Card, Empty, MicroLabel, QueryError, ViewHeader } from '../ui'
import { useMergeStatus } from '../data/queries'
import { mergePanelFrom, type MergePull, type MergeQueueable } from '../data/map/mergeStatus'

/**
 * Merge: what can I merge right now?
 *
 * Every dev-lane pull request is merged by hand, because a migration in one of
 * them reaches a production view. The question that costs the time is not how
 * to merge but which ones are safe to, and answering it means opening six pull
 * requests and reading their checks. A job on the box asks that every quarter
 * hour and writes the answers down; this page is the answers.
 *
 * It decides nothing. The verdict, the reason and the post-merge steps are the
 * job's words rendered as text, and every row links out to the pull request or
 * the ticket where the work actually happens -- there is no control on this
 * page, and merging stays in GitHub where a person does it.
 *
 * Nothing here is urgent (product rule 1). A pull request that has been not
 * ready since Friday sits in a muted clay word in the same type as a ready
 * one, there is no badge, and the header is a sentence rather than a count to
 * be cleared. And if the job stops running the banner says so, because a page
 * quietly offering this morning's answer about what is safe to merge is worse
 * than a page that admits it cannot tell.
 */
export function Merge() {
  const rows = useMergeStatus()
  // Both halves of the read are in the dependencies for the reason DEV-308
  // settled on the Status panel: reading `dataUpdatedAt` is what makes React
  // Query track it, so a poll that brought nothing new still re-renders, and
  // listing it is what makes the banner recompute when it does.
  const panel = useMemo(() => mergePanelFrom(rows), [rows.data, rows.dataUpdatedAt])

  if (rows.error) return <QueryError error={rows.error} />

  return (
    <>
      <ViewHeader title="Merge" aside={rows.data ? panel.headline : 'Reading the fleet…'} />

      {/* Above everything it qualifies: when the job has stopped publishing,
          every verdict below it is a claim about whenever it last ran. The
          Status panel's banner, in the same clay sentence -- this is the same
          statement about a different job. */}
      {panel.downSince ? (
        <Card style={{ marginBottom: 14 }}>
          <div className={css.statusDown}>
            {panel.downSince}
            <span className={css.statusDownNote}>
              Nothing below has been re-checked since then. GitHub has the current answer; this
              page cannot currently tell you what it is.
            </span>
          </div>
        </Card>
      ) : null}

      {!rows.data ? null
        : panel.generatedAt === null ? (
          <Card>
            <Empty glyph="[ ]">
              The merge-status job has not published a pass yet. This page fills itself the first
              time it runs — there is nothing to set up here.
            </Empty>
          </Card>
        ) : (
          <>
            <Card style={{ marginBottom: 14 }}>
              <div className={css.mergeHead}>
                <MicroLabel>Open pull requests</MicroLabel>
              </div>
              {panel.pulls.length === 0 ? (
                <Empty glyph="[-_-]">No pull request is open across the repositories the job watches.</Empty>
              ) : (
                <>
                  <div className={css.mergeRowHead}>
                    <MicroLabel>Pull request</MicroLabel>
                    <MicroLabel>CI</MicroLabel>
                    <MicroLabel>Mergeable</MicroLabel>
                    <MicroLabel>Review</MicroLabel>
                    <MicroLabel>Verdict</MicroLabel>
                  </div>
                  {panel.pulls.map((pull) => <PullRow key={pull.key} pull={pull} />)}
                </>
              )}
            </Card>

            {/* Shown even when empty, unlike the Status panel's attention
                list: nothing waiting to be queued is itself the answer to the
                question this section is read for, and a heading that vanishes
                looks like a section that failed to load. */}
            <Card>
              <div className={css.mergeHead}>
                <MicroLabel>Can be queued</MicroLabel>
              </div>
              {panel.queueable.length === 0 ? (
                <Empty glyph="[ ]">
                  No backlog ticket has had all of its blockers finished.
                </Empty>
              ) : (
                panel.queueable.map((t) => <QueueableRow key={t.key} ticket={t} />)
              )}
            </Card>
          </>
        )}
    </>
  )
}

/** The pull request, linking out where the job gave somewhere to go. */
function PullLabel({ pull }: { pull: MergePull }) {
  return pull.url
    ? <a href={pull.url} target="_blank" rel="noreferrer">{pull.label}</a>
    : <span>{pull.label}</span>
}

function PullRow({ pull }: { pull: MergePull }) {
  return (
    <div className={css.mergeRow}>
      <div>
        <div className={`${css.mergePr} mono`}>
          <PullLabel pull={pull} />
          {pull.ticketRef ? (
            <>
              {' '}
              {pull.ticketUrl
                ? <a href={pull.ticketUrl} target="_blank" rel="noreferrer">{pull.ticketRef}</a>
                : <span>{pull.ticketRef}</span>}
            </>
          ) : null}
        </div>
        {pull.title ? <div className={css.mergeTitle}>{pull.title}</div> : null}
      </div>
      <div className={css.mergeCell}>{pull.ci}</div>
      <div className={css.mergeCell}>{pull.mergeable}</div>
      <div className={css.mergeCell}>
        {pull.reviewState}
        {/* The reviewer agent's own verdict, under the humans' review state.
            Two different questions, and "approved · fail" is a real row. */}
        <div className={`${css.mergeMeta} mono`}>{pull.reviewer}</div>
      </div>
      <div>
        <div className={css.mergeCell} style={{ color: `var(--s-${pull.token})` }}>{pull.verdictLabel}</div>
        {/* The job writes a reason only when there is one to write, and it is
            the whole value of the row: "CI failing: typecheck" is what stops
            someone opening the pull request to find out. */}
        {pull.reason ? <div className={css.mergeReason}>{pull.reason}</div> : null}
      </div>
      {pull.postMerge.length > 0 ? (
        <div className={css.mergeSteps}>
          <MicroLabel>After merging</MicroLabel>
          {/* Keyed by position: the job writes each step once, but the key
              is this list's business and a repeated sentence is not a bug
              worth a dropped line. */}
          {pull.postMerge.map((step, i) => <div key={i} className={css.mergeStep}>{step}</div>)}
        </div>
      ) : null}
    </div>
  )
}

/** A backlog ticket with nothing left blocking it. Queued in Linear, not here. */
function QueueableRow({ ticket }: { ticket: MergeQueueable }) {
  return (
    <div className={css.mergeQueueRow}>
      <div className={`${css.mergePr} mono`}>
        {ticket.url
          ? <a href={ticket.url} target="_blank" rel="noreferrer">{ticket.ref}</a>
          : <span>{ticket.ref}</span>}
      </div>
      <div className={css.mergeQueueTitle}>{ticket.title ?? '—'}</div>
    </div>
  )
}
